/**
 * MONSTER TOWN — BUILDING SHELLS. OWNED BY THE WORLD CREW (src/world/**).
 *
 * One parameterised cosy building. The house, the shop, the ice cream shop and
 * the restaurant are all THE SAME CODE with different data — a new venue on the
 * street is a row in `src/world/layout.ts`, never an edit here. That is the
 * litmus the brief sets for content.
 *
 * Buildings are built in LOCAL space with the front door facing +Z, then rotated
 * by `front`. The returned record hands back everything the rest of the town
 * needs: the art, where the door is, where a monster stands to knock on it, and
 * the rectangle the monster must walk around.
 */

import * as THREE from 'three'
import { mix, type PaletteName } from '../core/palette'
import type { Vec3 } from '../core/types'
import { ball, box, boxOn, cylinder, flat, group, inked } from '../render/kit'
import { planter, signIcon, SIGN_BOARD, type SignIcon } from './props'

export type Facing = 'N' | 'E' | 'S' | 'W'

export interface BuildingSpec {
  readonly id: string
  /** Dev label only — never shown to a kid (LAW #4). */
  readonly devName: string
  /** Footprint centre, world space. */
  readonly cx: number
  readonly cz: number
  /** Size before rotation: w across the front, d back from the front. */
  readonly w: number
  readonly d: number
  readonly h: number
  /** Which way the front door faces. */
  readonly front: Facing
  readonly wall: PaletteName
  readonly trim: PaletteName
  readonly roof: PaletteName
  readonly roofStyle: 'gable' | 'flat'
  /** Two stripe colours turn the front into a shop awning. */
  readonly awning?: readonly [PaletteName, PaletteName]
  readonly sign: SignIcon | 'none'
  readonly chimney?: boolean
  readonly planterColor?: PaletteName
  /** Interior scene this door opens into, once that crew registers it. */
  readonly toScene?: string
  /** UI panel this door opens instead of a scene (the shop counter). */
  readonly panel?: string
}

export interface Rect {
  x0: number
  z0: number
  x1: number
  z1: number
}

export interface BuiltBuilding {
  readonly spec: BuildingSpec
  readonly art: THREE.Group
  /** World point of the door face. */
  readonly door: Vec3
  /** Where a monster stands to use the door. */
  readonly standAt: Vec3
  /** The door leaf, so a tap can make it wiggle. */
  readonly doorArt: THREE.Object3D
  readonly blocker: Rect
}

export function yawFor(front: Facing): number {
  switch (front) {
    case 'S':
      return 0
    case 'E':
      return Math.PI / 2
    case 'N':
      return Math.PI
    case 'W':
      return -Math.PI / 2
  }
}

// ---------------------------------------------------------------------------
// pieces
// ---------------------------------------------------------------------------

/** Chunky gable roof: two slanted slabs plus stepped gable ends. */
function gableRoof(
  g: THREE.Group,
  w: number,
  d: number,
  h: number,
  color: PaletteName,
  ink = true,
): void {
  const rise = Math.max(1.4, h * 0.6)
  /*
   * EAVES ARE 0.32, NOT 0.55, AND THEY HAVE A FASCIA.
   *
   * Round 2: "the yellow roof is a flat slab with no ridge and an overhang that
   * juts into space past the wall at the bottom-right." Both notes are the same
   * roof: a bare rotated slab with a wide overhang has nothing at its edge to
   * catch the light, so from a fixed iso camera it reads as one flat plane
   * hovering past the wall. A real roof has a fascia board along the eave and a
   * soffit underneath, and a ridge you can see. It now has all three.
   */
  const eave = 0.32
  const halfD = d / 2 + eave
  const slope = Math.hypot(halfD, rise)
  const angle = Math.atan2(rise, halfD)

  for (const sign of [1, -1]) {
    const slab = box(w + eave * 2, 0.3, slope, {
      color,
      y: h + rise / 2,
      z: (sign * halfD) / 2,
    })
    slab.rotation.x = sign * angle
    // The roof is half the building's silhouette; leaving it un-inked while the
    // walls were inked is what made the ink treatment look randomly applied.
    // Scenery cottages opt out: they sit 40 units away behind fog, where an ink
    // line is invisible and only costs an older iPad a draw call.
    g.add(ink ? inked(slab) : slab)
    // Fascia along the eave, in the trim-dark tone, so the roof edge is a board
    // and not a cut plane — this is what stops it reading as a floating slab.
    g.add(
      box(w + eave * 2 + 0.06, 0.3, 0.16, {
        color: 'woodDark',
        y: h - 0.08,
        z: sign * (d / 2 + eave),
      }),
    )
  }
  // Stepped gable ends read as triangles at this chunky scale and never leave
  // a gap the way a rotated slab pair does.
  // THREE steps, not five: at five the gable rake reads as a row of teeth along
  // the roof edge. Three chunky ones read as a deliberate stepped verge board,
  // which is the same vocabulary as everything else in this game.
  const steps = 3
  /*
   * ============ THE VERGE STANDS BESIDE THE SLAB, NOT THROUGH IT ============
   *
   * VR1: *"a thin dark-red diagonal hairline runs inside the roof plane from
   * about (455,375) to (530,300), where two roof quads meet"*, on the game's
   * most-photographed building.
   *
   * The steps were centred at `±(w + eave) / 2`, which is HALF AN EAVE INSIDE the
   * slab's own outer edge at `±(w/2 + eave)` — so about 0.16 of every step stood
   * up through the top surface of the roof. Both pieces are the same colour, but
   * they are not the same PLANE: one is sloped and one is upright, so the toon
   * shader gives them different bands and what a kid sees is a darker red line
   * ruled down the roof. It is not a z-fight and no depth tweak would have
   * touched it; the geometry really was crossing.
   *
   * The step now sits OUTBOARD, with a 0.05 overlap so its inner face is buried
   * in the slab rather than coplanar with it (coplanar is how you get the real
   * z-fight). Nothing of it reaches the roof plane, so there is nothing to draw
   * a line with. Narrower by the same amount it moved out, so the building's
   * silhouette is within 0.02 of what it was.
   */
  const sw = 0.26
  const vergeX = w / 2 + eave + sw / 2 - 0.05
  for (let i = 0; i < steps; i++) {
    const t = i / steps
    const zSpan = (d / 2 + eave) * (1 - t)
    for (const sx of [-1, 1]) {
      g.add(
        box(sw, rise / steps + 0.03, zSpan * 2, {
          color,
          x: sx * vergeX,
          y: h + rise * (t + 0.5 / steps),
        }),
      )
    }
  }
  // Ridge beam, standing proud of both slopes so it is visible from every one of
  // the four camera rotations — the "no ridge" half of the note.
  g.add(box(w + eave * 2 + 0.14, 0.26, 0.5, { color: 'woodDark', y: h + rise + 0.06 }))
  g.add(box(w + eave * 2 + 0.2, 0.16, 0.3, { color, y: h + rise + 0.2 }))
}

/**
 * Flat parapet roof — reads as a shop rather than a home.
 *
 * SITS ON THE WALL. Round 2: "the teal roof floats above the wall top with a
 * see-through gap." The cap slab was raised 0.24 above the wall line and made
 * wider than the wall, so from a low iso angle you looked straight under its
 * overhang and out the other side. The cap now starts BELOW the wall top and the
 * building gets a continuous cornice band that closes the join all the way
 * round, so there is no line of sight under the roof from any rotation.
 */
function flatRoof(g: THREE.Group, w: number, d: number, h: number, color: PaletteName): void {
  // Cornice: a band that overlaps the wall top by 0.2 and carries the overhang.
  g.add(box(w + 0.7, 0.34, d + 0.7, { color: 'wallCream', y: h - 0.14 }))
  g.add(box(w + 0.78, 0.14, d + 0.78, { color: 'woodDark', y: h + 0.05 }))
  // Deck, flush on top of the cornice. Paler than the parapet on purpose: a
  // whole roof in one saturated colour is the single largest block of colour a
  // low building presents to a fixed iso camera, and it swamps the walls.
  g.add(box(w + 0.4, 0.3, d + 0.4, { color: mix(color, 'wallCream', 0.34), y: h + 0.26 }))
  // Parapet: four walls that MEET at the corners (each long side runs the full
  // outer width), so the roof is a closed box rather than four floating slabs.
  const pw = w + 0.62
  const pd = d + 0.62
  for (const sz of [-1, 1]) {
    g.add(box(pw, 0.38, 0.3, { color, y: h + 0.56, z: (sz * pd) / 2 }))
  }
  for (const sx of [-1, 1]) {
    g.add(box(0.3, 0.38, pd, { color, x: (sx * pw) / 2, y: h + 0.56 }))
  }
  // Coping along the top of the parapet, one continuous ring.
  for (const sz of [-1, 1]) {
    g.add(box(pw + 0.12, 0.14, 0.42, { color: 'wallCream', y: h + 0.78, z: (sz * pd) / 2 }))
  }
  for (const sx of [-1, 1]) {
    g.add(box(0.42, 0.14, pd + 0.12, { color: 'wallCream', x: (sx * pw) / 2, y: h + 0.78 }))
  }
}

function frontWindow(g: THREE.Group, x: number, z: number, y: number, s: number, trim: PaletteName): void {
  g.add(box(1.7 * s, 1.7 * s, 0.22, { color: trim, x, y, z }))
  const glass = box(1.3 * s, 1.3 * s, 0.16, { color: 'glass', x, y, z: z + 0.08 })
  glass.material = flat('glass', 0.9)
  g.add(glass)
  g.add(box(1.35 * s, 0.13, 0.18, { color: trim, x, y, z: z + 0.12 }))
  g.add(box(0.13, 1.35 * s, 0.18, { color: trim, x, y, z: z + 0.12 }))
}

/**
 * Striped shop awning.
 *
 * IT REACHES BOTH CORNERS AND IT IS ONE PIECE. Round 2 saw "the pink/white
 * awning terminates mid-wall without reaching the corner" and "the two awning
 * segments do not meet". The strips themselves were always continuous; what
 * broke them was the sign board hanging in front on brackets (fixed below) and
 * a width of `w - 0.4`, which stopped 0.2 short of each corner post. It now runs
 * the full wall width, and gets end brackets and a valance so both ends land on
 * something instead of stopping in mid-air.
 */
function awningStrip(
  g: THREE.Group,
  w: number,
  z: number,
  y: number,
  colors: readonly [PaletteName, PaletteName],
): void {
  const n = Math.max(4, Math.round(w / 0.9))
  const stripW = w / n
  for (let i = 0; i < n; i++) {
    const strip = boxOn(stripW * 0.995, 0.26, 1.5, {
      color: i % 2 === 0 ? colors[0] : colors[1],
      x: -w / 2 + stripW * (i + 0.5),
      y,
      z: z + 0.75,
    })
    strip.rotation.x = -0.28
    g.add(strip)
  }
  // Head board against the wall — the thing the canopy is fixed to.
  g.add(box(w + 0.24, 0.3, 0.26, { color: 'wallCream', y: y + 0.5, z: z + 0.1 }))
  // End brackets, so the canopy is carried at both corners.
  for (const sx of [-1, 1]) {
    const arm = boxOn(0.16, 0.18, 1.5, { color: 'woodDark', x: (sx * w) / 2, y: y + 0.1, z: z + 0.75 })
    arm.rotation.x = -0.28
    g.add(arm)
  }
  // Valance along the front lip, which is what gives the awning an end.
  const lip = box(w + 0.2, 0.3, 0.14, { color: colors[1], y: y - 0.28, z: z + 1.45 })
  g.add(lip)
}

// ---------------------------------------------------------------------------
// the building
// ---------------------------------------------------------------------------

export function makeBuilding(spec: BuildingSpec): BuiltBuilding {
  const { w, d, h } = spec
  const g = group(`building:${spec.id}`)
  const front = d / 2

  // plinth + body
  g.add(boxOn(w + 0.6, 0.32, d + 0.6, { color: 'pathEdge' }))
  const body = boxOn(w, h, d, { color: spec.wall, y: 0.3 })
  g.add(inked(body))
  // corner posts give the flat colour some structure
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      g.add(
        boxOn(0.42, h, 0.42, {
          color: spec.trim,
          x: (sx * w) / 2,
          y: 0.3,
          z: (sz * d) / 2,
        }),
      )
    }
  }

  if (spec.roofStyle === 'gable') gableRoof(g, w, d, h + 0.3, spec.roof)
  else flatRoof(g, w, d, h + 0.3, spec.roof)

  // Door and windows scale with the wall so a 3.3-unit shop and a 4.6-unit
  // house both read correctly — the sizes used to be hard-coded and burst
  // through the eaves the moment the town was rescaled.
  const s = Math.min(1, h / 4.4)
  const doorH = Math.min(h - 0.55, 2.5 * s + 0.5)
  const doorW = 1.5 * s + 0.25
  const doorArt = group('doorLeaf')
  doorArt.position.set(0, 0, front + 0.05)
  doorArt.add(boxOn(doorW + 0.4, doorH + 0.3, 0.3, { color: spec.trim, y: 0.3, z: -0.06 }))
  doorArt.add(boxOn(doorW, doorH, 0.28, { color: 'doorWood', y: 0.3, name: 'leaf' }))
  doorArt.add(box(0.2, 0.2, 0.18, { color: spec.trim, x: doorW * 0.32, y: doorH * 0.62, z: 0.2 }))
  g.add(doorArt)
  g.add(boxOn(doorW + 1.1, 0.3, 1.2, { color: 'path', z: front + 0.7 }))

  // windows either side of the door
  const winX = Math.max(doorW + 1.2, w / 2 - 1.4)
  const winY = 0.3 + h * 0.62
  frontWindow(g, -winX, front + 0.04, winY, s, spec.trim)
  frontWindow(g, winX, front + 0.04, winY, s, spec.trim)
  // and one on each side wall so the building is not a facade
  for (const sx of [-1, 1]) {
    g.add(box(0.24, 1.5 * s, 1.5 * s, { color: spec.trim, x: (sx * w) / 2, y: winY, z: -d * 0.18 }))
    const sideGlass = box(0.18, 1.15 * s, 1.15 * s, {
      color: 'glass',
      x: (sx * (w + 0.12)) / 2,
      y: winY,
      z: -d * 0.18,
    })
    sideGlass.material = flat('glass', 0.9)
    g.add(sideGlass)
  }

  if (spec.awning !== undefined) awningStrip(g, w, front, h * 0.82, spec.awning)

  /*
   * Picture sign. FIXED TO THE WALL, NOT HANGING IN FRONT OF THE ROOF.
   *
   * Round 2 photographed the previous attempt exactly: "the white shop sign
   * hovers detached in front of the roof line with its mounting bracket ending
   * in mid-air", and the same brackets poked up above the board like antlers
   * because their top (boardY + 0.44*boardH) sat higher than the board's own top
   * edge (boardY + 0.5*boardH... at the sizes actually used, it did not). It
   * also floated in front of the awning and split it into two segments.
   *
   * There are no brackets any more. Every sign is a board on a backing plate
   * that STARTS at the wall face and finishes at the board — one solid piece of
   * geometry from the building to the picture, mounted in the gable (a house) or
   * above the awning but below the parapet (a shop). Nothing can hover.
   */
  if (spec.sign !== 'none') {
    const shopSign = spec.awning !== undefined
    const boardH = (shopSign ? 1.35 : 1.4) * s
    const boardY = shopSign ? h * 0.86 + boardH * 0.5 + 0.75 : h - 0.35
    const boardW = (shopSign ? 2.6 : 2.3) * s
    // The backing plate: from the wall face out to the board's back.
    const plateD = 0.46
    g.add(
      box(boardW * 0.62, boardH * 0.62, plateD, {
        color: spec.trim,
        y: boardY,
        z: front + plateD / 2,
      }),
    )
    const boardZ = front + plateD + 0.17
    const board = box(boardW, boardH, 0.34, { color: spec.trim, y: boardY, z: boardZ })
    g.add(inked(board))
    // Picture on BOTH faces + a cream inset panel behind it, so the sign never
    // presents a blank white plaque to a camera that cannot be walked around.
    for (const sz of [1, -1]) {
      // SIGN_BOARD, not `wallCream`: the house's own picture IS a cream house,
      // so a cream panel behind it rendered the sign blank except for its roof.
      // See the note on SIGN_BOARD in props.ts.
      g.add(
        box(boardW - 0.28, boardH - 0.24, 0.1, {
          color: SIGN_BOARD,
          y: boardY,
          z: boardZ + sz * 0.2,
        }),
      )
      const icon = signIcon(spec.sign)
      icon.position.set(0, boardY, boardZ + sz * 0.32)
      icon.rotation.y = sz > 0 ? 0 : Math.PI
      icon.scale.setScalar(0.66 * s)
      g.add(icon)
    }
  }

  // planters make a front door look loved
  const pc = spec.planterColor ?? 'flowerPink'
  for (const sx of [-1, 1]) {
    const p = planter(pc)
    p.position.set(sx * (winX + 0.1), 0, front + 0.9)
    g.add(p)
  }

  if (spec.chimney === true) {
    /*
     * THE CHIMNEY MUST CLEAR THE RIDGE. Round 1 sized it from the WALL height
     * and started the smoke at h*1.35, which for a gable roof is inside the
     * roof: the first white puff intersected the red slabs and shipped as "a
     * white sphere clipping through a roof" in three separate frames. Both the
     * stack and the smoke are now measured off the actual ridge height.
     */
    const rise = spec.roofStyle === 'gable' ? Math.max(1.4, (h + 0.3) * 0.6) : 0.9
    const ridgeY = h + 0.3 + rise
    const stackTop = ridgeY + 0.5
    const cx2 = w * 0.3
    const cz2 = -d * 0.22
    g.add(boxOn(1.0, stackTop - h * 0.5, 1.0, { color: 'wood', x: cx2, y: h * 0.5, z: cz2 }))
    g.add(boxOn(1.18, 0.24, 1.18, { color: 'woodDark', x: cx2, y: stackTop - 0.24, z: cz2 }))
    /*
     * SMOKE THAT DRIFTS. Round 2: "the chimney plume is three or four identical
     * white circles evenly spaced in a vertical line — static bubbles, no size
     * or opacity variation." Real smoke gets bigger, fainter and further off
     * line as it rises, and the gaps between puffs open up. Five puffs on a
     * curve, each one larger and more transparent than the last, on a spacing
     * that grows — same cost, and it reads as smoke instead of beads.
     */
    const puffs = 5
    for (let i = 0; i < puffs; i++) {
      const t = i / (puffs - 1)
      const puff = ball(0.24 + t * 0.42, {
        color: 'flowerWhite',
        x: cx2 + Math.sin(t * 2.6) * 0.85 + t * 0.35,
        y: stackTop + 0.32 + t * t * 2.4 + t * 1.1,
        z: cz2 - Math.sin(t * 1.9) * 0.6,
        seg: 7,
      })
      puff.material = flat('flowerWhite', 0.8 - t * 0.5)
      puff.castShadow = false
      puff.receiveShadow = false
      g.add(puff)
    }
  }

  // place into the world
  const yaw = yawFor(spec.front)
  g.position.set(spec.cx, 0, spec.cz)
  g.rotation.y = yaw
  const sy = Math.sin(yaw)
  const cy = Math.cos(yaw)
  const door: Vec3 = { x: spec.cx + sy * front, y: 0, z: spec.cz + cy * front }
  const reach = front + 2.4
  const standAt: Vec3 = { x: spec.cx + sy * reach, y: 0, z: spec.cz + cy * reach }
  const halfX = spec.front === 'E' || spec.front === 'W' ? d / 2 : w / 2
  const halfZ = spec.front === 'E' || spec.front === 'W' ? w / 2 : d / 2
  const blocker: Rect = {
    x0: spec.cx - halfX - 0.4,
    z0: spec.cz - halfZ - 0.4,
    x1: spec.cx + halfX + 0.4,
    z1: spec.cz + halfZ + 0.4,
  }

  return { spec, art: g, door, standAt, doorArt, blocker }
}

/** A tiny closed cottage for street density. No door, pure skyline. */
export function cottage(
  cx: number,
  cz: number,
  wall: PaletteName,
  roof: PaletteName,
  yaw = 0,
): { art: THREE.Group; blocker: Rect } {
  const g = group('cottage')
  const w = 6.5
  const d = 5.5
  const h = 3.2
  g.add(boxOn(w + 0.4, 0.28, d + 0.4, { color: 'pathEdge' }))
  g.add(inked(boxOn(w, h, d, { color: wall, y: 0.26 })))
  gableRoof(g, w, d, h + 0.26, roof, false)
  g.add(boxOn(1.2, 1.9, 0.24, { color: 'doorWood', y: 0.26, z: d / 2 + 0.02 }))
  frontWindow(g, -2.0, d / 2 + 0.02, 2.0, 0.75, 'wallCream')
  frontWindow(g, 2.0, d / 2 + 0.02, 2.0, 0.75, 'wallCream')
  // Measured off the ridge, not the wall — same bug as the house chimney.
  const cotRidge = h + 0.26 + Math.max(1.4, (h + 0.26) * 0.6)
  g.add(
    cylinder(0.45, cotRidge + 0.55 - h * 0.6, {
      color: 'wood',
      x: w * 0.3,
      y: h * 0.6,
      z: -d * 0.2,
      seg: 6,
    }),
  )
  g.position.set(cx, 0, cz)
  g.rotation.y = yaw
  const swap = Math.abs(Math.sin(yaw)) > 0.5
  const hx = (swap ? d : w) / 2 + 0.4
  const hz = (swap ? w : d) / 2 + 0.4
  return { art: g, blocker: { x0: cx - hx, z0: cz - hz, x1: cx + hx, z1: cz + hz } }
}
